import {
  Box,
  TextField,
  Avatar,
  Typography,
  Autocomplete,
  useTheme,
  createFilterOptions,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import React, { useState } from 'react';
import { Sparkles, ChevronDown } from 'lucide-mui';
import { Member, SpeakerType } from '../../../../../../types';
import { RootState } from '../../../../../../reducers';
import { getLetterAvatar } from '../../../../../../lib/commonUtils';
import { updateSpeaker } from '../../../../selectedVideoSlice';
import { FilePreviewRouteParams } from '../../../../types';

function formatSpeakerName(
  name: string,
  postProcessSpeakers?: Array<Record<string, string>>
) {
  if (postProcessSpeakers) {
    const speakerMatch = postProcessSpeakers.find((speaker) => speaker[name]);
    if (speakerMatch) {
      return {
        name: speakerMatch[name],
        isAISuggestion: true,
        originalName: name,
      };
    }
  }

  const match = name.match(/^SPEAKER_(\d+)$/);
  if (match) {
    return {
      name: `Speaker ${parseInt(match[1], 10) + 1}`,
      isAISuggestion: false,
      originalName: name,
    };
  }

  return {
    name,
    isAISuggestion: false,
    originalName: name,
  };
}

interface SpeakerSelectProps {
  speaker: SpeakerType;
  speakerId: string;
}

type MemberOption =
  | Member
  | {
      inputValue: string;
      name: string;
      isAISuggestion?: boolean;
      picture?: string;
      isClearOption?: boolean;
    };

const filter = createFilterOptions<MemberOption>();

const SpeakerAvatar = ({
  name,
  picture,
  isAI,
}: {
  name: string;
  picture?: string;
  isAI?: boolean;
}) => (
  <Avatar
    alt={name}
    src={picture}
    sx={{ width: 28, height: 28, bgcolor: isAI ? 'secondary.main' : undefined }}
  >
    {isAI ? (
      <Sparkles
        sx={{ fontSize: '0.875rem', color: 'secondary.contrastText' }}
      />
    ) : (
      getLetterAvatar(name)
    )}
  </Avatar>
);

const SpeakerName = ({ name, isAI }: { name: string; isAI: boolean }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Typography
      variant="body2"
      sx={{
        color: isAI ? 'secondary.contrastText' : 'text.secondary',
        fontSize: '0.875rem',
        lineHeight: 1.43,
      }}
    >
      {name}
    </Typography>
  </Box>
);

const SpeakerSelect = ({ speaker, speakerId }: SpeakerSelectProps) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { teamId, fileId } = useParams<FilePreviewRouteParams>();
  const { team, selectedVideo } = useSelector((state: RootState) => state);
  const { teamSpaceMembers = [] } = team.selectedTeam || {};

  const [isEditing, setIsEditing] = useState(false);
  const [inputTextValue, setInputTextValue] = useState('');

  // Parse AI suggestions
  let postProcessSpeakers;
  try {
    postProcessSpeakers = selectedVideo.postProcess?.systemPromptResponses
      ?.SUMMARY_AND_PROMPTS?.responseText
      ? JSON.parse(
          selectedVideo.postProcess.systemPromptResponses.SUMMARY_AND_PROMPTS
            .responseText
        ).speakers
      : undefined;
  } catch (error) {
    console.error('Error parsing postProcessSpeakers:', error);
    postProcessSpeakers = undefined;
  }

  const formattedSpeakerName = formatSpeakerName(
    speaker.name,
    postProcessSpeakers
  );
  const formattedSpeakerId = formatSpeakerName(speakerId, postProcessSpeakers);
  const displayName = formattedSpeakerName.name || formattedSpeakerId.name;
  const isAISuggestion =
    (formattedSpeakerName.isAISuggestion &&
      displayName === formattedSpeakerName.name) ||
    (formattedSpeakerId.isAISuggestion &&
      displayName === formattedSpeakerId.name);

  const aiSuggestion = postProcessSpeakers?.find(
    (s: Record<string, string>) => s[speakerId]
  )?.[speakerId];

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: MemberOption | null
  ) => {
    if (!teamId || !fileId) return;

    if (
      newValue === null ||
      (newValue && 'inputValue' in newValue && newValue.isClearOption)
    ) {
      dispatch(
        updateSpeaker({
          teamId,
          fileId,
          speakerId,
          speaker: {
            name: speakerId,
            userId: undefined,
            picture: undefined,
          },
        })
      );
    } else if ('inputValue' in newValue) {
      dispatch(
        updateSpeaker({
          teamId,
          fileId,
          speakerId,
          speaker: {
            name: newValue.inputValue,
            userId: undefined,
            picture: undefined,
          },
        })
      );
    } else {
      const { name, picture, _id: userId } = newValue;
      dispatch(
        updateSpeaker({
          teamId,
          fileId,
          speakerId,
          speaker: { userId, name, picture },
        })
      );
    }
    setIsEditing(false);
  };

  const filterOptions = (options: MemberOption[], params: any) => {
    const filtered = filter(options, params);
    const { inputValue: input } = params;

    if (
      input !== '' &&
      !options.some((option) => 'name' in option && input === option.name)
    ) {
      filtered.push({
        inputValue: input,
        name: `Add "${input}"`,
      });
    }

    if (aiSuggestion && speaker.name !== aiSuggestion) {
      filtered.unshift({
        inputValue: aiSuggestion,
        name: aiSuggestion,
        isAISuggestion: true,
      });
    }

    if (speaker.name && !speaker.userId && !aiSuggestion) {
      const exists = filtered.some(
        (option) =>
          ('inputValue' in option && option.inputValue === speaker.name) ||
          ('name' in option && option.name === speaker.name)
      );

      if (!exists) {
        filtered.push({
          inputValue: speaker.name,
          name: speaker.name,
          isAISuggestion,
        });
      }
    }

    return filtered;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        columnGap: theme.spacing(0.75),
        cursor: 'pointer',
        padding: theme.spacing(0.75, 1.5, 0.75, 0.5),
        borderRadius: '24px',
        height: '40px',
        transition: theme.transitions.create(['background-color', 'width'], {
          duration: theme.transitions.duration.shorter,
        }),
        width: isEditing ? '300px' : 'auto',
        backgroundColor: isEditing ? theme.palette.action.hover : 'transparent',
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      }}
      onClick={() => setIsEditing(true)}
    >
      <SpeakerAvatar
        name={displayName}
        picture={speaker.picture}
        isAI={isAISuggestion}
      />

      {isEditing ? (
        <Autocomplete
          value={
            teamSpaceMembers.find((member) => member._id === speaker.userId) ||
            null
          }
          onChange={handleChange}
          options={teamSpaceMembers}
          open={isEditing}
          onOpen={() => {}}
          onBlur={() => setIsEditing(false)}
          autoHighlight
          clearOnBlur={false}
          clearOnEscape={false}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            if ('inputValue' in option) return option.inputValue;
            return option.name;
          }}
          filterOptions={filterOptions}
          freeSolo
          inputValue={inputTextValue}
          onInputChange={(event, newInputValue, reason) => {
            setInputTextValue(newInputValue);
            if (reason === 'clear' || (!newInputValue && aiSuggestion)) {
              handleChange(event as React.SyntheticEvent, {
                inputValue: aiSuggestion || '',
                name: aiSuggestion || '',
                isAISuggestion: true,
              });
            }
          }}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(1),
                minHeight: '40px',
                padding: theme.spacing(1, 2),
              }}
            >
              <SpeakerAvatar
                name={'inputValue' in option ? option.inputValue : option.name}
                picture={'inputValue' in option ? undefined : option.picture}
                isAI={Boolean('inputValue' in option && option.isAISuggestion)}
              />
              <SpeakerName
                name={'inputValue' in option ? option.inputValue : option.name}
                isAI={Boolean('inputValue' in option && option.isAISuggestion)}
              />
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              fullWidth
              autoFocus
              placeholder="Select or type a name"
              inputProps={{
                ...params.inputProps,
                'aria-label': 'Select speaker',
                style: {
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  padding: 0,
                  fontSize: '0.875rem',
                  lineHeight: 1.43,
                },
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem',
                  lineHeight: 1.43,
                  padding: 0,
                },
              }}
            />
          )}
          sx={{
            width: '300px',
            marginLeft: '-6px',
            '& .MuiInputBase-root': {
              height: '40px',
              padding: theme.spacing(0, 0.75, 0, 0.75),
              backgroundColor: 'transparent',
              fontSize: '0.875rem',
              lineHeight: 1.43,
              '& fieldset': { border: 'none' },
              '&:hover fieldset': { border: 'none' },
              '&.Mui-focused fieldset': { border: 'none' },
              '& input': {
                fontSize: '0.875rem !important',
                lineHeight: '1.43 !important',
              },
            },
            '& .MuiAutocomplete-input': {
              fontSize: '0.875rem !important',
              lineHeight: '1.43 !important',
            },
            '& .MuiAutocomplete-endAdornment': {
              position: 'absolute',
              right: theme.spacing(0.75),
              height: '100%',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiAutocomplete-clearIndicator': {
              padding: 0,
              marginRight: 0,
              color: theme.palette.text.secondary,
              '& svg': {
                width: 16,
                height: 16,
              },
            },
            '& .MuiAutocomplete-popupIndicator': {
              display: 'none',
            },
            '& .MuiAutocomplete-popper': {
              marginTop: '4px !important',
              width: '300px !important',
            },
            '& .MuiAutocomplete-paper': {
              width: '100%',
              marginLeft: 0,
              borderRadius: '24px',
              backgroundColor: theme.palette.action.hover,
              boxShadow: 'none',
            },
            '& .MuiAutocomplete-listbox': {
              padding: theme.spacing(0.75, 0),
              '& .MuiAutocomplete-option': {
                minHeight: '40px',
                padding: theme.spacing(0.75, 1.5, 0.75, 0.5),
                '&[aria-selected="true"]': {
                  backgroundColor: theme.palette.action.selected,
                },
                '&.Mui-focused': {
                  backgroundColor: theme.palette.action.focus,
                },
              },
            },
          }}
        />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SpeakerName name={displayName} isAI={Boolean(isAISuggestion)} />
          <ChevronDown
            style={{
              width: 16,
              height: 16,
              marginLeft: 4,
              color: theme.palette.text.secondary,
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default SpeakerSelect;
